import { useState, useCallback, useRef, useEffect } from 'react';

/** Timeout in ms for each attempt (initial + retry). */
const REQUEST_TIMEOUT_MS = 8_000;

/** Max retry count after network-level failures. */
const MAX_RETRIES = 1;

/**
 * Determines whether an error is a network-level failure that indicates a
 * stale/dead socket — the kind that a single retry will fix after the
 * browser's network stack wakes up.
 */
function isNetworkError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return true;
  }
  if (err instanceof TypeError) {
    const msg = err.message.toLowerCase();
    return msg.includes('failed to fetch') || msg.includes('network');
  }
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return (
      msg.includes('failed to fetch') ||
      msg.includes('networkerror') ||
      msg.includes('load failed') ||
      msg.includes('the operation was aborted') ||
      msg.includes('abort')
    );
  }
  // Supabase postgrest wrapped error string checks
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = String((err as Record<string, unknown>).message).toLowerCase();
    if (msg.includes('abort')) return true;
  }
  return false;
}

export interface ResilientActionResult<TArgs extends unknown[], TResult> {
  isLoading: boolean;
  error: string | null;
  execute: (...args: TArgs) => Promise<TResult | null>;
  clearError: () => void;
}

/**
 * A generic hook that wraps any async action (like an RPC call or insert)
 * with "Zero-Ping Resilience":
 * 1. An AbortController hard timeout of 8 seconds.
 * 2. Instant silent retries on network failures (which wakes dead sockets).
 * 3. Foolproof loading state cleanup.
 * 
 * @param actionFn A callback that takes an AbortSignal as its first argument, followed by any other args.
 */
export function useResilientAction<TArgs extends unknown[], TResult>(
  actionFn: (signal: AbortSignal, ...args: TArgs) => Promise<TResult>
): ResilientActionResult<TArgs, TResult> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeController = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (activeController.current) {
        activeController.current.abort();
        activeController.current = null;
      }
    };
  }, []);

  const execute = useCallback(
    async (...args: TArgs): Promise<TResult | null> => {
      if (isLoading) return null;

      setIsLoading(true);
      setError(null);

      if (activeController.current) {
        activeController.current.abort();
        activeController.current = null;
      }

      let lastError: unknown = null;

      try {
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            const controller = new AbortController();
            activeController.current = controller;

            const timeoutId = setTimeout(
              () => controller.abort(),
              REQUEST_TIMEOUT_MS,
            );

            try {
              const result = await actionFn(controller.signal, ...args);
              clearTimeout(timeoutId);

              if (import.meta.env.DEV && attempt > 0) {
                console.log(`[useResilientAction] Action succeeded on attempt ${attempt + 1}`);
              }

              return result;
            } catch (innerErr) {
              clearTimeout(timeoutId);
              throw innerErr;
            }
          } catch (err) {
            lastError = err;

            if (isNetworkError(err) && attempt < MAX_RETRIES) {
              if (import.meta.env.DEV) {
                console.warn(
                  `[useResilientAction] Attempt ${attempt + 1} failed (network). Retrying immediately...`,
                  err,
                );
              }
              continue;
            }

            break;
          }
        }

        if (lastError instanceof Error) {
          if (isNetworkError(lastError)) {
            const msg = 'TIMEOUT|Request timed out. Please check your connection and try again.';
            setError(msg);
            throw new Error(msg);
          }
          setError(lastError.message);
          throw lastError;
        }

        // Supabase often returns { message, details } objects directly as errors
        if (lastError && typeof lastError === 'object' && 'message' in lastError) {
           const errObj = lastError as { message: string };
           setError(errObj.message);
           throw new Error(errObj.message);
        }

        const fallbackMsg = 'Request failed. Please try again.';
        setError(fallbackMsg);
        throw new Error(fallbackMsg);
      } finally {
        setIsLoading(false);
        activeController.current = null;
      }
    },
    [isLoading, actionFn]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    isLoading,
    error,
    execute,
    clearError
  };
}
